import React, { useCallback, useEffect, useState } from 'react'
import './Category.scss'
import { Params, useNavigate, useParams } from 'react-router-dom'
import { ErrorActions, ErrorCodes, LocalStorageItem, RowTypes, SessionStorageItem } from '../../constants'
import { addLastFocusedItemToSS, getActiveProfile, onBackKeyPress } from '@utils/helpers'
import SpatialNavigation from '@utils/spatial-navigation.js'
import Card from '@components/Cards/Card'
import EmptyListContainer from '@components/EmptyListContainer/EmptyListContainer'
import { getCategoryResults } from '@services/category'
import Skeleton from '@pages/Skeleton/Skeleton'
import { focusableRoutes } from '@constants/routes.const'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import { setError } from '@actions/errorCodeActions'
import { useDispatch } from 'react-redux'
import { VelvetSDK } from 'sdk'
import { APIError, CardItem, CategoryData } from 'sdk/services/cms/types/cmsService'
import { useTypedSelector } from 'store'

interface CategoryProps {
  showMenu: (status: boolean) => void
}

const Category = ({ showMenu }: CategoryProps) => {
  const SN = SpatialNavigation
  const { chosenCategory } = useParams<Params>()
  const [categoryCards, setCategoryCards] = useState<CardItem[] | CategoryData>()
  const [categoryError, setCategoryError] = useState<APIError | null>()
  const userObject =
    localStorage.getItem(LocalStorageItem.USER_DATA) &&
    JSON.parse(localStorage.getItem(LocalStorageItem.USER_DATA) as string)
  const userId = userObject?.userID
  const activeProfile = getActiveProfile()
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const V = VelvetSDK.Vocabulary.vocabulary
  const userCountry = useTypedSelector((state) => state.userCountry.data?.countryCode)
  const lastFocusedCard = JSON.parse(sessionStorage.getItem(SessionStorageItem.LAST_FOCUSED_ITEM_CATEGORY) as string)

  const navigateToHomePage = useCallback(
    (e: KeyboardEvent) =>
      onBackKeyPress(e, () => {
        navigate(-1)
        sessionStorage.removeItem(SessionStorageItem.LAST_FOCUSED_ITEM_CATEGORY)
      }),
    [navigate],
  )

  useEffect(() => {
    document.addEventListener('keydown', navigateToHomePage, true)

    return () => {
      document.removeEventListener('keydown', navigateToHomePage, true)
    }
  }, [navigateToHomePage])

  const categoryData = useCallback(() => {
    activeProfile?.preferredLanguage &&
      chosenCategory &&
      getCategoryResults(chosenCategory, userCountry, activeProfile?.preferredLanguage).then((results) => {
        if ((results as CategoryData).error) {
          setCategoryError((results as CategoryData).error)
        } else {
          setCategoryCards(results)
          categoryError && setCategoryError(null)
        }
        setIsLoading(false)
      })
  }, [activeProfile?.preferredLanguage, chosenCategory, userCountry, categoryError])

  useEffect(() => {
    setIsLoading(true)
    userId && categoryData()
  }, [chosenCategory, dispatch, categoryData, userId])

  useEffect(() => {
    if (categoryError) {
      setIsLoading(false)
      dispatch(
        setError({
          showErrorModal: true,
          HTTPcode: categoryError.status,
          errorCode: `${ErrorCodes.FETCH_CATEGORY_PAGE}`,
          fetchAction: `${ErrorActions.CATEGORY}/${chosenCategory}`,
        }),
      )
    }
  }, [categoryError, chosenCategory, dispatch])

  useEffect(() => {
    showMenu(false)
    if (categoryCards) {
      const findLastFocusedCard =
        (categoryCards as CardItem[]).length > 0 &&
        (categoryCards as CardItem[]).find((category) => category.id === lastFocusedCard)

      lastFocusedCard && findLastFocusedCard ? SN.focus(`#${lastFocusedCard}`) : SN.focus(focusableRoutes.CATEGORY)
    }

    return () => showMenu(true)
  }, [categoryCards, SN, showMenu, lastFocusedCard])

  return (
    <div className="category-page">
      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          {categoryCards && (categoryCards as CardItem[]).length === 0 && (
            <>
              <Text tag={TextTag.P} className="category-title">
                {chosenCategory}
              </Text>
              <EmptyListContainer
                firstSubtitle={V.categoriesPageEmptyTitle}
                secondSubtitle={V.categoriesPageEmptyDescription}
                buttonTitle={V.categoriesPageEmptyButtonText}
              />
            </>
          )}
          {categoryCards && (categoryCards as CardItem[]).length > 0 && (
            <>
              <Text tag={TextTag.P} className="category-title">
                {chosenCategory}
              </Text>
              <div className="card-wrapper">
                {(categoryCards as CardItem[]).map((element, index: number) => {
                  return (
                    <Card
                      key={element.id}
                      item={element}
                      rowType={RowTypes.MYLIST}
                      index={index}
                      isFocusable
                      lastFocusedCard={() => addLastFocusedItemToSS(element.id || '0', focusableRoutes.CATEGORY)}
                    />
                  )
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default Category
